import { useState } from 'react';
import { X, Save, Eye, EyeOff } from 'lucide-react';
import { TemplatePreview } from './TemplatePreview';

interface Template {
  label: string;
  description?: string;
  slots: string[];
  layout: {
    hasSidebar: boolean;
  };
  recommended: string[];
}

interface Slot {
  label: string;
  description: string;
  position: string;
  allowsMultiple: boolean;
}

interface TemplateFormDialogProps {
  templateId: string | null;
  template?: Template;
  availableSlots: Record<string, Slot>;
  availableSections: string[];
  existingTemplateIds: string[];
  onSave: (templateId: string, template: Template) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

export function TemplateFormDialog({
  templateId,
  template,
  availableSlots,
  availableSections,
  existingTemplateIds,
  onSave,
  onCancel,
  isSaving,
}: TemplateFormDialogProps) {
  const isEditing = !!templateId;

  // Form state
  const [id, setId] = useState(templateId || '');
  const [label, setLabel] = useState(template?.label || '');
  const [description, setDescription] = useState(template?.description || '');
  const [selectedSlots, setSelectedSlots] = useState<string[]>(template?.slots || []);
  const [hasSidebar, setHasSidebar] = useState(template?.layout.hasSidebar || false);
  const [recommended, setRecommended] = useState<string[]>(template?.recommended || []);
  const [showPreview, setShowPreview] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // ID validation
    if (!id.trim()) {
      newErrors.id = 'ID richiesto';
    } else if (!/^[a-z0-9-]+$/.test(id)) {
      newErrors.id = 'ID deve contenere solo lettere minuscole, numeri e trattini';
    } else if (!isEditing && existingTemplateIds.includes(id)) {
      newErrors.id = 'ID già esistente';
    }

    // Label validation
    if (!label.trim()) {
      newErrors.label = 'Label richiesto';
    }

    // Slots validation
    if (selectedSlots.length === 0) {
      newErrors.slots = 'Seleziona almeno uno slot';
    }

    // Check that all selected slots exist
    selectedSlots.forEach((slotId) => {
      if (!availableSlots[slotId]) {
        newErrors.slots = `Slot "${slotId}" non trovato`;
      }
    });

    // Check that all recommended sections exist
    recommended.forEach((sectionId) => {
      if (!availableSections.includes(sectionId)) {
        newErrors.recommended = `Sezione "${sectionId}" non trovata`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const templateData: Template = {
      label: label.trim(),
      description: description.trim() || undefined,
      slots: selectedSlots,
      layout: {
        hasSidebar,
      },
      recommended,
    };

    try {
      await onSave(id, templateData);
    } catch (error) {
      console.error('Save error:', error);
      setErrors({ submit: 'Errore durante il salvataggio' });
    }
  };

  const handleSlotToggle = (slotId: string) => {
    setSelectedSlots((prev) =>
      prev.includes(slotId)
        ? prev.filter((id) => id !== slotId)
        : [...prev, slotId]
    );
  };

  const handleRecommendedToggle = (sectionId: string) => {
    setRecommended((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Build preview data
  const previewData = {
    id,
    label: label || 'Nuovo Template',
    description,
    slots: selectedSlots,
    layout: { hasSidebar },
    recommended,
  };

  const slotEntries = Object.entries(availableSlots);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Modifica Template' : 'Nuovo Template'}
            </h2>
            {isEditing && (
              <p className="text-sm text-gray-500 mt-1">Template ID: {templateId}</p>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="btn-secondary flex items-center space-x-2"
            >
              {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span>{showPreview ? 'Nascondi' : 'Anteprima'}</span>
            </button>
            <button
              onClick={onCancel}
              className="p-2 text-gray-400 hover:text-gray-600 rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className={`grid ${showPreview ? 'grid-cols-2' : 'grid-cols-1'} gap-6 p-6`}>
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Template ID */}
              <div>
                <label htmlFor="template-id" className="block text-sm font-medium text-gray-700 mb-1">
                  Template ID *
                </label>
                <input
                  id="template-id"
                  type="text"
                  value={id}
                  onChange={(e) => setId(e.target.value.toLowerCase())}
                  disabled={isEditing}
                  className={`input w-full ${errors.id ? 'border-red-500' : ''}`}
                  placeholder="home-page"
                />
                {errors.id && <p className="text-xs text-red-600 mt-1">{errors.id}</p>}
                <p className="text-xs text-gray-500 mt-1">
                  Formato: lettere minuscole, numeri, trattini. Es: home-page
                </p>
              </div>

              {/* Label */}
              <div>
                <label htmlFor="template-label" className="block text-sm font-medium text-gray-700 mb-1">
                  Label *
                </label>
                <input
                  id="template-label"
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  className={`input w-full ${errors.label ? 'border-red-500' : ''}`}
                  placeholder="Home Page"
                />
                {errors.label && <p className="text-xs text-red-600 mt-1">{errors.label}</p>}
              </div>

              {/* Description */}
              <div>
                <label htmlFor="template-desc" className="block text-sm font-medium text-gray-700 mb-1">
                  Descrizione
                </label>
                <textarea
                  id="template-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="input w-full"
                  rows={3}
                  placeholder="Descrizione del template..."
                />
              </div>

              {/* Slots Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slots * ({selectedSlots.length} selezionati)
                </label>
                <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                  {slotEntries.map(([slotId, slot]) => (
                    <label
                      key={slotId}
                      className="flex items-start p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSlots.includes(slotId)}
                        onChange={() => handleSlotToggle(slotId)}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">{slot.label}</span>
                          <span className="text-xs text-gray-500 font-mono">{slotId}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-0.5">{slot.description}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                            {slot.position}
                          </span>
                          {slot.allowsMultiple && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                              multiple
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                {errors.slots && <p className="text-xs text-red-600 mt-1">{errors.slots}</p>}
              </div>

              {/* Layout Settings */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Layout</label>
                <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasSidebar}
                    onChange={(e) => setHasSidebar(e.target.checked)}
                  />
                  <div>
                    <span className="font-medium text-gray-900">Sidebar</span>
                    <p className="text-sm text-gray-600">Include una sidebar laterale</p>
                  </div>
                </label>
              </div>

              {/* Recommended Sections */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sezioni Raccomandate (opzionale)
                </label>
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                  {availableSections.length === 0 ? (
                    <p className="text-sm text-gray-500 p-3">Nessuna sezione disponibile</p>
                  ) : (
                    availableSections.map((sectionId) => (
                      <label
                        key={sectionId}
                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <input
                          type="checkbox"
                          checked={recommended.includes(sectionId)}
                          onChange={() => handleRecommendedToggle(sectionId)}
                          className="mr-3"
                        />
                        <span className="text-sm text-gray-900 font-mono">{sectionId}</span>
                      </label>
                    ))
                  )}
                </div>
                {errors.recommended && (
                  <p className="text-xs text-red-600 mt-1">{errors.recommended}</p>
                )}
              </div>

              {/* Submit Error */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{errors.submit}</p>
                </div>
              )}
            </form>

            {/* Preview */}
            {showPreview && (
              <div className="border-l border-gray-200 pl-6">
                <TemplatePreview
                  template={previewData as any}
                  availableSlots={availableSlots}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="btn-secondary"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="btn-primary flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>{isSaving ? 'Salvataggio...' : 'Salva Template'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
